/**
 * Case studies content.
 *
 * Each case study is a self-contained object. To add one, copy any entry,
 * change the `slug` (used in the URL: /case-studies/<slug>), and edit fields.
 *
 * MEDIA — every section can have a `media` array of images and/or videos.
 * Drop image files into `/public/case-studies/<study-slug>/` and reference
 * them as e.g. `/case-studies/<study-slug>/image-name.jpg`. For videos,
 * either drop an .mp4 into the same folder or paste a YouTube / Vimeo URL.
 *
 * PLACEHOLDERS — leave `src` undefined on any media item and the page will
 * render a labelled placeholder block until you add the real asset.
 */

/** A single image OR video block within a case-study section. */
export type CaseStudyMedia =
  | {
      kind: 'image';
      /** Image URL. Omit to render a labelled placeholder. */
      src?: string;
      /** Required for accessibility — also doubles as the placeholder label. */
      alt: string;
      /** Optional caption shown below the image. */
      caption?: string;
      /** Aspect ratio override, e.g. '16 / 9'. Defaults to '16 / 9'. */
      aspectRatio?: string;
    }
  | {
      kind: 'video';
      /**
       * Video URL. Three formats supported:
       *  - Local file:    /case-studies/<slug>/walkthrough.mp4
       *  - YouTube:       https://www.youtube.com/watch?v=…
       *  - Vimeo:         https://vimeo.com/…
       * Omit to render a labelled placeholder.
       */
      src?: string;
      /** Provider override; auto-detected from URL if omitted. */
      provider?: 'native' | 'youtube' | 'vimeo';
      /** Poster image (optional) — shown before native video plays. */
      poster?: string;
      /** Required for accessibility — also doubles as the placeholder label. */
      alt: string;
      /** Optional caption shown below the video. */
      caption?: string;
      /** Aspect ratio override. Defaults to '16 / 9'. */
      aspectRatio?: string;
    };

export interface CaseStudySection {
  /** Section heading shown in the case study layout */
  heading: string;
  /** Paragraphs of body copy */
  paragraphs: string[];
  /** Optional bullet list */
  bullets?: string[];
  /** Optional images and videos. Render in order. */
  media?: CaseStudyMedia[];
  /**
   * Layout for the media grid:
   *  - 'stack' (default) — single column, full-width per item
   *  - '2col'            — two columns on viewports ≥ 700px,
   *                        single column on mobile
   * Use 2col when the images are tall and a stacked layout would balloon
   * the page length.
   */
  mediaLayout?: 'stack' | '2col';
}

export interface CaseStudyMetric {
  label: string;
  value: string;
  detail?: string;
}

/**
 * Section the case study belongs to on the homepage:
 *  - 'ux'        → main "Selected work" grid (UX / UI led)
 *  - 'creative'  → "Creative Archive" grid (creative led, more visual)
 *
 * Defaults to 'ux' if omitted.
 */
export type CaseStudyCategory = 'ux' | 'creative';

export interface CaseStudy {
  /** URL slug — must be unique */
  slug: string;
  /** Which homepage section this study belongs to. Defaults to 'ux'. */
  category?: CaseStudyCategory;
  /** Card + page title */
  title: string;
  /** Card subtitle / one-liner */
  summary: string;
  /** Tag chips on card and detail page */
  tags: string[];
  /** Year shown on card */
  year: string;
  /** Client / brand name (e.g. "Confidential retailer") */
  client: string;
  /** Role on the project */
  role: string;
  /**
   * Card thumbnail. Two-tone gradient placeholder — swap for real imagery.
   * `position` overrides the default centred crop (useful for tall page
   * screenshots where you want the header/top of the page in the thumbnail).
   * Accepts any CSS background-position value, e.g. 'top', 'center', '50% 20%'.
   */
  thumbnail: {
    src?: string;
    alt: string;
    gradient: [string, string];
    position?: string;
  };
  /** Hero image on the case study page (large banner above the body). */
  hero?: { src?: string; alt: string };
  /**
   * Structured sections.
   *  - `overview` is required (every case study has at least a brief).
   *  - `approach`, `solution` and `impact` are optional — useful for
   *    creative-archive pieces where the format is more visual / lighter on
   *    process narrative.
   */
  overview: CaseStudySection;
  goal?: CaseStudySection;
  approach?: CaseStudySection;
  solution?: CaseStudySection;
  impact?: CaseStudySection;
  /** Pull-out metrics shown above the impact section */
  metrics?: CaseStudyMetric[];
  /** Optional prototype embed (Figma / loom / video URL) */
  prototype?: { url: string; label: string };
}

export const caseStudies: CaseStudy[] = [
  {
    slug: 'gravity-design-system',
    title: 'Designing at scale',
    summary:
      'Building a design system to deliver scalable, consistent and accessible experiences across a multi-brand e-commerce platform.',
    tags: ['Design systems', 'Tokens', 'Accessibility', 'E-commerce'],
    year: '2024',
    client: 'THG. Multi-brand e-commerce platform',
    role: 'Design System Lead',
    thumbnail: {
      src: '/case-studies/gravity-design-system/designing_at_scale_card.png',
      alt: 'Gravity design system — overview thumbnail',
      gradient: ['#050241', '#0096FA'],
    },
    hero: {
      src: '/case-studies/gravity-design-system/designing_at_scale.png',
      alt: 'Gravity. Design system hero shot showing tokens, components and layout in use',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'Altitude is a multi-tenant e-commerce platform powering over 100 brands, each with their own digital guidelines. Without a shared foundation, designers were producing inconsistent wireframes, copying patterns from file to file, and shipping subtle variations that fragmented the customer experience and slowed engineering down.',
        'A discovery workshop with design, engineering and product surfaced six recurring problems that all pointed to the same root cause: there was no defined core for teams to build on.',
      ],
      bullets: [
        'Inefficient design: wireframes inconsistent, patterns copied and adapted under deadline pressure',
        'Accessibility compliance gaps: Colour contrast, focus states and patterns inconsistent across sites',
        'Styling inconsistencies: No clear rules for type sizes, colour usage, spacing or interaction',
        'Inconsistent user experiences: Components and behaviours varied between pages, raising cognitive load',
        'Engineering debt: Sites took longer to build because patterns weren’t reusable',
        'Not scalable: The platform couldn’t improve without cementing the foundation',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/discovery.png',
          alt: 'Sample of findings from the discovery workshop',
          caption: 'Six themes from the discovery workshop, mapped to design and engineering pain.',
        },
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/planning.png',
          alt: 'Planning phase for the design system, showing the roadmap and key deliverables, and how it will be applied to the platform.',
          caption: 'Planning phase for the design system, understanding how it will be applied to the platform.',
        },
      ],
    },
    approach: {
      heading: 'Approach',
      paragraphs: [
        'Gravity is the platform’s core design system, built in collaboration with the engineering squads. Aligned to the Tailwind framework and built on top of ShadCN, it provides reusable web components and design elements for every digital team working on storefronts on the Altitude platform.',
        'Gravity is structured in four layers: Foundations, Components, Patterns & Widgets and Templates. The system acts as a single source of truth, aligning distributed teams around a shared direction while remaining flexible enough to accommodate each brand’s identity.',
      ],
      bullets: [
        'Foundations: colour tokens, typography, spacing, grids and accessibility standards',
        'Components: reusable UI elements (buttons, inputs, cards). 80+ master components',
        'Patterns: meaningful interactions: navigation, product & editorial cards and form flows',
        'Widgets and templates: configurable content blocks for core journeys (homepage, PLP, PDP, basket, checkout)',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/structure.png',
          alt: 'Diagram of Gravity’s four-layer structure: Foundations, Components, Patterns, Widgets & Templates',
          caption: 'The four-layer structure underpinning every storefront on the platform.',
        },
      ],
    },
    solution: {
      heading: 'Solution',
      paragraphs: [
        'Foundations are tokenised and Tailwind-aligned.', 
        'Colour primitives: Primary, Secondary, Tertiary and Neutral. Tint and Shade values are defined from 50–950, inline with the Tailwind framework to give flexibility for all use cases.',
        'Typography uses two responsive scales (small <600px, large ≥600px), each with seven sizes and two styles, for consistent hierarchy across devices. Layout sits on a 12-column grid with breakpoints at 600/900/1200px and a 1400px max width on large screens, all anchored to an 8px core spacing unit (4px line-height increments to keep typography in vertical rhythm).',
        'The component library has 80+ master components. Buttons, Inputs, Selectors, Accordions, Navigation, Messaging, Image-ratio containers and more, all EAA compliant by default. Update a master once and the change flows through every brand instantly, while still letting designers tailor specific brands.',
        'Reusable patterns. Navigational systems, Product and Editorial cards, Form flows, and 21 configurable widgets, from Hero banners to Video, that content teams can compose without developer input. A "white-label" suite of page templates (Homepage, PLP, PDP, Basket) provides a starting point for new brands; each is founded from competitor analysis and structured around real consumer browsing behaviour.',
        'Documentation lives alongside every layer. It explains when and how to use each piece, with do’s and don’ts, measurements, accessibility requirements (contrast, focus, keyboard) and the configurable parameters that make every component brand-flexible.',
      ],
      bullets: [
        '80+ accessible master components',
        '21 configurable widgets covering content, commerce and editorial',
        'Tokenised colour, typography, spacing and grid foundations',
        'Page templates for Homepage, PLP, PDP and Basket',
        'Documentation embedded into every layer',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/Colour_foundation.png',
          alt: 'Foundations — colour palette showing primary, secondary, tertiary and neutral scales (50–950)',
          caption:
            'Colour primitives across four palettes, all WCAG-contrast compliant and consumed via semantic tokens.',
        },
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/Typography.png',
          alt: 'Foundations — typography scales for small and large devices',
          caption: 'Two responsive type scales. Seven sizes, two styles, with 4px line-height increments.',
        },
       
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/Layout.png',
          alt: 'Foundations — 12-column grid and 8px spacing system',
          caption: 'Grid, breakpoints and the 8px core unit underpinning every layout.',
        },
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/Components.png',
          alt: 'Component library — sample of buttons, inputs, selectors and messaging',
          caption: 'A snapshot of some of the 80+ master components.',
        },
        {
          kind: 'image',
          alt: 'Patterns and widgets — PDP and PLP composition examples',
          src: '/case-studies/gravity-design-system/patterns_&_widgets.png',
          caption: 'Patterns and widgets in use, building out Product Listing and Product Detail experiences.',
        },
        {
          kind: 'video',
          src: '/case-studies/gravity-design-system/homepage.mp4',
          alt: 'Walkthrough — composing a homepage from Gravity widgets',
          caption: 'Walkthrough: composing a homepage end-to-end using Gravity widgets and templates.',
        },
      ],
    },
    metrics: [
      { label: 'Master components', value: '80+', detail: 'across foundations, forms and commerce' },
      { label: 'Configurable widgets', value: '21', detail: 'composable by content teams' },
      { label: 'Faster filter use', value: '+30%', detail: 'horizontal-filter PLP pattern' },
      { label: 'Accessibility', value: 'WCAG AA', detail: 'compliant by default' },
    ],
    impact: {
      heading: 'Impact',
      paragraphs: [
        'Gravity is the foundation for every storefront on the Altitude platform. Brand teams onboard faster, content teams reorder and configure widgets without engineering input, and core accessibility is a property of the system rather than a project deliverable.',
        'The horizontal-filter PLP pattern, validated through user research, lets customers apply filters around 30% faster while shortening page length, improving both browsing speed and product discoverability.',
        'Configuration changes to colour, fonts and typescale, combined with creative widget composition, produce visibly distinct aesthetics for each brand from a single shared codebase.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/gravity-design-system/application.png',
          alt: 'Flexibility in action. Three brand variants of the same template',
          caption:
            'One source template, three brand configurations. The brand configurations, typography and widget choices do the work.',
        },
      ],
    },
  
  },

  {
    slug: 'responsive-website-builder',
    title: 'Responsive Website Builder',
    summary:
      'A website creation tool built to reduce e-commerce design time through configuration-driven prototyping.',
    tags: [ 'Figma', 'FigmaMake', 'Claude', 'Vercel', 'VS Code','Supabase','E-commerce', 'Prototyping','Design systems', 'Design tools'],
    year: '2026',
    client: 'Internal tool',
    role: 'Product & UX Designer',
    thumbnail: {
      src: '/case-studies/responsive-template-builder/responsive-thumbnail.png',
      alt: 'Responsive Template Builder — website creator tool thumbnail',
      gradient: ['#050241', '#6366f1'],
    },
    hero: {
      src: '/case-studies/responsive-template-builder/template-builder-hero.png',
      alt: 'Responsive Template Builder. Brand control panel and site preview',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'Creating responsive wireframes and click-through prototypes for every prospective client, is an essential, but often time-consuming task for designers.',
        'Using a combination of Figma, and AI tools, I created a responsive template builder to reduce the time and effort required. Using FigmaMake, Claude, VS Code and Supabase, the tool gives designers a faster, more structured way to build e-commerce journeys. Instead of building pages from templates in the Platform toolkit, designers can create responsive wireframes and interactive prototypes for all breakpoints, reducing production time on average by 2 days.',
      ],
    },
    goal: {
      heading: 'The Goal',
      paragraphs: [
        'Reduce the time and effort required for creating responsive wireframes and interactive prototypes, enabling designers to work on new platform features and strategic improvements:',
      ],
      bullets: [
        'Deliver a tool that allows designers to build responsive wireframes and interactive prototypes for e-commerce journeys.', 
        'Aim to reduce production time by an average of 2 days per project.',
        'The tool should be user-friendly, flexible, and capable of handling a wide range of design requirements while maintaining consistency across projects.', 
        'MVP to include core features such as brand management, card styling, widget building, and page-specific builders for key e-commerce pages.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/responsive-template-builder/configuration.png',
          alt: 'Defining the variables for the colour tokens in the Brand Control Panel',
          caption: 'When using the design toolkit, configuring the brand variables in Figma is a manual process.',
        },
      ],
    },
    approach: {
      heading: 'The Approach',
      paragraphs: [
        'My first step was to import each of the core Figma wireframe templates into Figma Make. Once completed, I used targeted prompts to transform the static designs into fully responsive pages.',
        'The first challenge that emerged, was discovering that the majority of components used to create the pages contained hard-coded values rather than configurable properties.',
        'This led to a different approach: I implemented a token, variable, and skin import system that allowed me to deconstruct static designs and rebuild them with dynamic, reusable foundations.',
        'This created a cascading design system where updates made at foundation level could automatically propagate throughout the prototype.',
        'The next step was to create a Brand Control Panel, providing a centralised interface for managing colour tokens and fonts that automatically propagate across all components and pages.',
        'From this foundation, additional configuration layers were systematically introduced. These are comprised of Card Styles, Widget Builder, Navigation, Footer, and Page-Specific Builders. Each section inherited and extended the same underlying token structure, ensuring consistency while still allowing flexible customisation.',
        'The core journey pages were then reconstructed using these newly created configuration tools.',
        'One of the main challenges during this process was managing the AI\'s token usage. Often, a prompt would introduce unexpected changes, requiring correction prompts to fix errors that the AI introduced. Token usage limits were often being reached, slowing progress.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/responsive-template-builder/planning.png',
          alt: 'Planning workshop sketches showing the structure of the tool and its core features',
          caption: 'Planning process for the tool,to define the scope and requirements.',
        },
      ],
    },
    
    solution: {
      heading: 'The Solution',
      paragraphs: [
        'The Responsive Website Builder is a modular, configuration-driven approach for building and presenting a client site. Rather than manually creating static wireframes and a simple clickthrough prototype, designers work with a live, interactive design environment.',
        'The tool allows designers to apply the client colour palette, typography and brand language, style product and editorial cards, build widgets, core journey pages and present a site that looks and feels like a finished product. ',
      ],
      bullets: [
        'Manages brand identity and color system',
        'Easily configure Product and editorial card styling variations',
        'Build and configure content widgets from a library of 7 core widgets',
        'Build and configure the core journey: Homepage, PLP, PDP and basket pages',
        'Style and build in a live preview environment, with changes reflected in real-time across all pages',
        '',
        '**Key Features & Configurability:**',
        '**Brand Control Panel**', 
        'The foundation of the design system, providing a visual interface for managing the complete color palette, fonts, typographic scale and CTA styling',
        'Live Token System: Colors automatically update all design tokens and components in real-time',
        'Comprehensive Color Palettes: Primary, Secondary and Tertiary colour palette, automatically configured, and all individually editable. Neutral, Messaging and Pricing.',
        'Instant Propagation: All changes apply across the entire site immediately',
        'Reset to Default option for one click restoration of default settings',
        '',
        '**Card Styling System**',
         'Product and Editorial cards are the visual foundation of e-commerce sites. The Card Styles panel provides granular control over card appearance',
        'Bordered Cards: Modern contained appearance with configurable rounded corners and padding.',
        'Borderless Cards: Clean, editorial aesthetic where images extend full-width',
        'Edge-to-Edge Images: Minimalist option where image bleeds to card edges',
        'Card Surface Colors: Choose from neutral, brand palette shades, or custom hex values',
        'Live Preview: Changes are immediately visible in a sample card, showing exactly how products will appear on the live site',
        '',
        '**Widget Builder & Widget Library**', 
        'Widgets are reusable content sections, that can be placed on the homepage or category landing pages',
        'Widget Builder: 8 Pre-built configurable content widgets, to create content for pages.',
        'Widget Library: Created widgets are available in the library for reuse across pages and projects, ensuring consistency and efficiency.',
        'Drag-and-Drop Configuration: Arrange widgets, set spacing, and control visibility',
        'Responsive Behavior: Widgets automatically adapt to different screen sizes',
        '',
        '**Page-Specific Builders. Dedicated builders for each major page type:**',
        'Homepage Builder: Design hero sections, promotional content, widget layouts, and featured product areas',
        'Product Listing Page (PLP) Manager: Configure grid layouts, filtering options, sorting, product card display, and pagination',
        'Product Page Builder: Grid and carousel image layout options, information accordions, variant selectors, pricing, and related products',
        'Basket Page Builder: Configure cart item display, summary information, checkout buttons, and order details',
        'Navigation Builder: Create main primary navigation, secondary category dropdowns, and breadcrumbs',
        'Footer Builder: Build footer content, links, and information sections',
        '',
        '**Image Management. Images can be sourced in two ways:**',
        'Direct Upload: Upload product and editorial images directly into the tool',
        'Free Image Libraries: Connected image library integrations allow designers to browse and insert free-to-use images for prototyping',
        '',
        '**Real-Time Preview & Multi-Page Navigation.**',
        'The tool provides an integrated preview.',
        'Designers can switch between pages (Homepage, Product Listing, Product Detail, Basket)',
        'See all styling changes reflected in real-time',
        'Preview responsive behavior across different screen sizes',
        'Test navigation flows',
        '',
        '**How Styling Decisions Flow Through the System**', 
        'Brand Control Panel (Colors) → Card Styles (Card Layout + Surface Colors) → Widget Styling (Inherits colors + card styles) → All Page Instances (Homepage, PLP, Product, Basket).', 
        '**As an example:** A designer changes primary color from green to orange in Brand Control Panel; All buttons, links, and primary CTAs update across the site. Designer updates product card style from "Bordered" to "Borderless"; All product cards in the PLP and homepage widgets reflect the new style.',
      ],
      mediaLayout: 'stack',
      media: [
        {
          kind: 'video',
          src: '/case-studies/responsive-template-builder/configurator.mp4',
          alt: 'Control panel sections shown,  to configure all aspects of styling and content',
          caption: 'Control panels for configuring and building the site.',
        },
        {
          kind: 'video',
          src: '/case-studies/responsive-template-builder/colour-change.mp4',
          alt: 'Brand Control Panel showing live token system and application of colour changes across the site',
          caption: 'Brand Control Panel: Adjusting the primary color from green to orange. Value propogates across all design tokens and components in real-time.',
        },
        {
          kind: 'video',
          src: '/case-studies/responsive-template-builder/PLP-cards.mp4',
          alt: 'Card Styles panel showing product card layout options and live preview',
          caption: 'Card Styles: Designers control layout (Bordered, Borderless, Edge-to-Edge) and surface colors. Card content can be toggled on or off. Drag and drop reordering.',
        },
        {
          kind: 'video',
          src: '/case-studies/responsive-template-builder/basket.mp4',
          alt: 'Basket preview showing product added to basket and adding an upsell carousel',
          caption: 'Adding products to basket, with realtime price updaing and addition of upsell carousel.',
        },
        {
          kind: 'video',
          src: '/case-studies/responsive-template-builder/editing-widget.mp4',
          alt: 'Editing a campaign banner to change the image and text content',
          caption: 'Editing a widget: changing the image, headline and body text of a campaign banner widget, with changes reflected in the live preview.',
        },
        {
          kind: 'video',
          src: '/case-studies/responsive-template-builder/responsive.mp4',
          alt: 'Switching between device types in the preview to show responsive behavior',
          caption: 'Responsive Design: The site adapts to different screen sizes and devices in real-time.',
        },
      ],
    },
    metrics: [
      { label: 'Design time savings', value: '2 days', detail: 'Reduced from traditional wireframing' },
      { label: 'Configurable widgets', value: '21+', detail: 'composable by content teams' },
      { label: 'Master components', value: '80+', detail: 'aligned with the platform code across foundations, forms and commerce' },
      { label: 'Accessibility', value: 'WCAG 2.2 AA', detail: 'built into every component' },
    ],
    impact: {
      heading: 'Impact & Results',
      paragraphs: [
        '**Time Savings**: Traditional approach requires 3 to 5 days of Figma wireframing per project. With the Template Builder, configuration and interactive prototyping can be done in 1 to 2 days. Result: An approximate 2-day reduction in design phase timeline.',
        '**Quality Improvements**: Design consistency through centralized brand and styling controls that eliminate manual inconsistencies. Content-aware design with real images and realistic product data informing design decisions earlier. Stakeholder Communication through interactive prototypes that communicate design intent better than static wireframes. Design-to-Dev Alignment with responsive behavior and styling logic baked into the prototype.',
        '**Team Efficiency**: Designer productivity increases by spending more time on strategic design decisions, less on repetitive wireframing. Cross-functional alignment allows stakeholders to see realistic site structure and behavior early. Development handoff provides clear, intention and behaviour rather than static comps.',
      ],
      bullets: [
        '**For Designers**: Faster iteration with visual feedback. Instant updates mean seeing design decisions in context immediately. ',
        '**Automation:**. Design system rules are driven by the tool. Content Integration options, enables rapid design with real images and data.',
        '**For Stakeholders**: Realistic previews show exactly how the site will look and behave. Fast feedback loops. Iterate on design concepts quickly.',
        '**For Development Teams**: Clear definition of stying and behviour. Design aligned to code. Designers have already determined which components are needed and how they\'re styled.',
      ],
      
    },
  },
  {
    slug: 'myprotein-referrals',
    title: 'Lifting referrals at MyProtein',
    summary:
      'Increase account sign up and revenue, driven from the Myprotein referral programme.',
    tags: ['Referrals', 'Account sign up', 'Awareness'],
    year: '2025',
    client: 'Myprotein',
    role: 'Lead UX Designer',
    thumbnail: {
      src: '/case-studies/myprotein-referrals/myp_thumbnail.png',
      alt: 'MyProtein referrals case study thumbnail',
      gradient: ['#0a0a0a', '#cc1d20'],
    },
    hero: {
      src: '/case-studies/myprotein-referrals/referrals_hero.png',
      alt: 'Myprotein referrals. As-is customer journey across the funnel',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'The refer-a-friend scheme at Myprotein was underperforming. Monthly revenue had declined from £1.07m in 2024 to £900k in 2025, creating a projected £2m annual shortfall.',
        'The brief: Audit the product from end-to-end, identify key friction points and solve the issues to increase sign-ups.',
        'The work structured around three north-star metrics. Referral participation rate (customers sharing links), Referral sign-up rate (referred users registering), and Referral traffic volume (overtall reach). All tracked post-launch.',
      ],
    },
    approach: {
      heading: 'Approach',
      paragraphs: [
        'I reviewed the existing funnel end-to-end as a customer, mapping every touchpoint where a referral could begin, be discovered, or be redeemed. The results showed that the programme lacked information and visibility in some key areas of the user journey.',
        'Specific friction surfaced quickly: the account-home → referrals-page journey was broken on web; current customer credit was hidden in the account dropdown; the footer link existed but offered no information without further user action. The link took users to a referrals login page, with no information about the scheme, and the new customer signup form asked for seven fields, where every benchmarked competitor (Bodybuilding Warehouse, Bulk, We are Feel, Protein Works) required only three. ECRM messaging was inconsistent with on-site, and there was no recovery path for users who pasted a referral code in the wrong place.',
        'Competitor benchmarking shaped the design hypotheses. The strongest performers surface the offer prominently above the footer, give the sharer one-tap copy, plus pre-populated email/SMS/WhatsApp, and treat the new customer landing page as a referral benefits page rather than a form gate.',
        'The proposed tactics were scoped against Now, Next, Later. Easy wins that could be implemented immediately, what needs more discovery, and what needs technical discussion. Thirteen numbered tactics were taken through to design.',''
      ],
      bullets: [
        'As-is audit of the full referral funnel (web, app, ECRM)',
        'Competitor benchmarking, identify best-in-class.',
        'Three KPIs defined: participation rate, sign-up rate, traffic volume',
        '13 design tactics, scoped now / more-discovery / technical-discussion',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/comp-analysis.png',
          alt: 'referral journey flows for competitor sites',
          caption: 'Competitor analysis to benchmark referral journeys.',
        },
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/ideation-tactics.png',
          alt: 'UX review and tactical design ideas — initial quick fire ideation session',
          caption: 'UX review and tactical design ideas — initial quick fire ideation mapping session, structured around the three north-star metrics.',
        },
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/as-is-journey.png',
          alt: 'As-is customer journey showing where the referral programme surfaces today',
          caption: 'The as-is journey, referral touchpoints buried at the moments they most needed to land.',
        },
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/ecrm.png',
          alt: 'ECRM email touchpoints. Credit-to-spend and invitation messaging',
          caption: 'ECRM touchpoints. Messaging inconsistent with on-site, value of the offer underplayed.',
        },
      ],
    },
    solution: {
      heading: 'Solution',
      paragraphs: [
        'Thirteen coordinated tactics were planned across the funnel, each with clear, mappable goals to address the friction identified in the audit.',
        'Tasks: A redesigned referrals page in My Account; a footer that surfaces the benefits; current credit made visible in the account dropdown and account home banner; marketing widgets across landing pages; a refer-a-friend landing page that promotes the benefits of signing up; a simplified new-customer signup form; sharing methods reworked with pre-populated methods (one-tap copy, email, SMS, WhatsApp); consistent messaging across web and ECRM; and a sharable link in the  order-confirmation.',
        'A recovery flow identifying when customers paste the referral code at basket instead of at signup, surfacing help, and correcting their path rather than failing silently.',
        'Mobile and desktop prototypes were built end-to-end for stakeholder review, walking through the new account, signup, share and redeem journeys.',
      ],
      bullets: [
        '13 numbered tactics across account, footer, landing, signup, sharing, ECRM, post-purchase',
        'Signup form: 7 fields → 3, matching the competitor benchmark',
        'Sharing: one method → four (copy, email, SMS, WhatsApp), pre-populated',
        'Recovery flow for referral codes pasted at the wrong step',
        'Two interactive prototypes: mobile and desktop for stakeholder walk-through',
      ],
      mediaLayout: '2col',
      media: [
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/refer-logged-in.png',
          alt: 'Desktop Refer-a-friend flow for a new customer',
          caption: 'Desktop. Refer-a-friend flow for a new customer with multi-method sharing.',
        },
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/Referee-New-Customer.png',
          alt: 'Desktop Referee flow for a new customer',
          caption: 'Desktop. Referee flow for a new customer.',
        },
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/Mobile-Refer-a-friend.png',
          alt: 'Mobile Refer-a-friend flow for a logged-in customer',
          caption: 'Mobile. Refer-a-friend flow for a logged-in customer with multi-method sharing.',
        },
        {
          kind: 'image',
          src: '/case-studies/myprotein-referrals/order-confirmation.jpg',
          alt: 'Order confirmation page with new referral widget',
          caption: 'Order confirmation screen. Promotion of sharing the referral post-purchase.',
        },
        
        // {
       
        //  kind: 'video',
        //   // src: '/case-studies/myprotein-referrals/mobile-prototype.mp4',  // ← drop the recording in here
        //   alt: 'Mobile prototype walkthrough, referrals journey end-to-end',
        //   caption: 'Mobile prototype, the new referrals journey end-to-end.',
        // },
        // {
        //   kind: 'video',
        //   // src: '/case-studies/myprotein-referrals/desktop-prototype.mp4',  // ← drop the recording in here
        //   alt: 'Desktop prototype walkthrough, referrals journey end-to-end',
        //   caption: 'Desktop prototype — the new referrals journey end-to-end.',
        // },
      ],
    },  
    
    metrics: [
       {
        label: 'Referral participation rate',
        value: '+17%',
        detail: '% of customers sharing a link',
      },
      {
        label: 'Referral sign-up rate',
        value: '+12%',
        detail: 'measured 90 days post-launch',
      },
     
      {
        label: 'Revenue',
        value: '£18,360',
        detail: 'monthly uplift in the first 90 days',
      },
    ],
    impact: {
      heading: 'Impact',
      paragraphs: [
        'The thirteen tactics shipped as a coordinated update across the referrals funnel. The experience is now coherent for the first time. Referrals surface at the moments they were missing (account UI, footer, post-purchase, ECRM), and the new-customer signup matches what competitors are doing rather than asking for more than is needed.',
        'The first 90 days recorded a revenue uplift of £18,360 attributed to the referrals scheme particpation. Next step is to prioritise the second-wave tactics flagged for further discovery, and run a quarterly review.',
      ],
    },
  },


  
  {
    slug: 'matalan-product-discoverability',
    title: 'Improving product discoverability',
    summary:
      'Restructuring Matalan’s navigation across desktop and mobile. Making a vast product catalogue easy to explore, reducing user frustration and improving discoverability.',
    tags: ['E-commerce', 'UX research', 'Information architecture', 'Navigation'],
    year: '2024',
    client: 'Matalan',
    role: 'Lead UX Designer',
    thumbnail: {
      src: '/case-studies/matalan-product-discoverability/ML_discoverability_card.png',
      alt: 'Matalan navigation case study thumbnail',
      gradient: ['#0a0a0a', '#ff5a1f'],
    },
    hero: {
      src: '/case-studies/matalan-product-discoverability/ML_discoverability.png',
      alt: 'Matalan three-tier navigation across desktop and mobile',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'Matalan’s product catalogue is vast, with extensive ranges across clothing, footwear and homeware. Improving product discoverability was one of the migration’s core objectives.',
        'The brief: how might we simplify and restructure the navigation to make Matalan’s extensive range easier to explore, helping customers find relevant products quickly while reducing complexity and cognitive load?',
      ],
    },
    approach: {
      heading: 'Approach',
      paragraphs: [
        'Discovery research with 10 participants revealed key pain points in Matalan’s navigation. Each participant was tasked with finding five products using the live site, sharing feedback as they went. Common feedback centred around the difficulty to find the items they were being asked to search for, complaining that the navigation was overwhelming. Many items were duplicated (‘gift card’ appeared more than five times), and several categories were grouped illogically, ‘pet products’ sat under Kitchen & Laundry.',
        'Competitor analysis and benchmarking identified retailers who led at presenting large, diverse product catalogues. Sites with intuitive categories and predictable sub-categories. These benchmarks shaped the design hypotheses we then took into ideation.',
        'A 100-participant card-sort study via UserZoom, across Womens, Mens, Kids and Homeware, let real users group products into the categories that felt intuitive to them. Working with the client, we developed new navigation variants that removed duplicates, aligned to user expectations, and introduced an additional tier to reduce overwhelm in the primary navigation.',
        'Two restructured options were tree-tested alongside the existing navigation: option one with smaller, specific groupings, and option two with broader categories. The tests measured findability, task success and backtracking. Both new variants significantly outperformed the existing navigation, with the three-tier structure and reduced duplication improving product location.',
      ],
      bullets: [
        'Discovery study: 10 participants, 5-task findability test on the live site',
        'Competitor benchmarking against best-in-class large-catalogue retailers',
        'Card sort: 100 participants via UserZoom across 4 departments',
        'Tree test: 3 navigation variants compared on findability, task success and backtracking',
      ],
      media: [
      
        {
          kind: 'video',
          src: '/case-studies/matalan-product-discoverability/MLvideo.mp4',
          alt: 'Walkthrough of the existing Matalan navigation structure pre-redesign',
          caption: 'The existing navigation. Overwhelming category counts and duplicates surfacing throughout.',
        },
        {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/benchmarking.png',
          alt: 'Competitor benchmarking of navigation structures across leading retailers, identifying best-in-class patterns for large product catalogues',
          caption: 'Competitor benchmarking of leading retailers with large product catalogues, identifying positive and negative patterns.',
        },
        {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/ML_treetest.png',
          alt: 'Tree test results comparing the existing navigation against two restructured variants',
          caption: 'Tree-test results — both new variants outperformed the existing navigation on findability and task success.',
        },
      ],
    },
    solution: {
      heading: 'Solution',
      paragraphs: [
        'The design phase started with low-fidelity wireframes for desktop and mobile, focussing on content hierarchy using insights gained from the tree-test results. Particular attention was paid to transitions between and within categories on mobile, so users always had clear context of where they were in the journey, reducing friction and disorientation.',
        'Regular workshop sessions with the client, provided feedback for iterative refinements; requests were broken down into deliverable milestones aligned with the engineering team’s roadmap.', 
        'Working closely with Matalan’s in-house UX team, we progressed to high-fidelity designs that applied the digital brand guidelines and product imagery, then built interactive prototypes for usability testing.',
        'A 30 participant unmoderated UserZoom study, recruited to match Matalan’s target audience, evaluated the proposed designs against the live site on both mobile and desktop. Users were asked first to share their expectations, then completed task-based scenarios on both designs and gave preference feedback.',
        'The three-tier structure was strongly preferred. It let users explore Matalan’s diverse categories without feeling overwhelmed, guiding them through clear, logical steps to their destination. Despite the extra step, participants felt it improved their ability to locate items efficiently. Fewer initial categories meant lower cognitive load and faster decision-making.',
      ],
      bullets: [
        'Low-fi wireframing → high-fi designs across desktop and mobile',
        'Mobile transitions designed for in-context wayfinding',
        'Iterative client workshops; deliverables aligned to engineering roadmap',
        'Usability test: 30 participants, unmoderated, mobile + desktop',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/workshop.png',
          alt: 'FigJam workshop session collaborating with the Matalan team on navigation hierarchy',
          caption: 'Iterative workshops with the Matalan team in FigJam, breaking work into engineering-aligned milestones.',
        },
        {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/tooling.png',
          alt: 'FigJam workshop session to scope tooling requirements and feature gaps.',
          caption: 'Internal tooling scoping and identifying feature gaps.',
        },
        {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/desktop_nav.png',
          alt: 'Desktop flyout navigation, showing the new three-tier hierarchy',
          caption: 'Desktop flyout — the new three-tier hierarchy, with reduced primary category count.',
        },
         {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/planning.png',
          alt: 'Phased delivery plan, showing the milestones for desktop and mobile navigation updates',
          caption: 'Phased delivery plan, showing the milestones for desktop and mobile navigation updates.',
        },
        {
          kind: 'video',
          src: '/case-studies/matalan-product-discoverability/mobile_navigation.mp4',
          alt: 'Mobile navigation walkthrough showing in-context transitions between categories',
          caption: 'Mobile navigation — clear context maintained as users move between and within categories.',
        },
        {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/usability.png',
          alt: '3 tier navigation designs and user feedback. The new design was strongly preferred for its improved clarity and reduced overwhelm.',
          caption: '3 tier navigation delivery milestones and sample user testing feedback.',
        },
      ],
    },
    /*metrics: [
      { label: 'User frustration', value: '–50%', detail: 'Content Square data' },
      { label: 'Time to product page', value: '–10s', detail: 'first month, desktop' },
      { label: 'Card sort participants', value: '100', detail: 'across 4 departments' },
      { label: 'Tree test variants', value: '3', detail: 'including the existing structure' },
    ], */
    impact: {
      heading: 'Impact',
      paragraphs: [
        'In the first month after the redesigned navigation went live, Matalan saw a marked increase in desktop revenue — expected given the significant departure from the previous design. More importantly, customers were reaching product pages an average of ten seconds faster than before, and Content Square data recorded a 50% reduction in user frustration.',
        'There was a regression in mobile navigation load time post-launch. The next step is to optimise that surface so the speed gains seen on desktop carry through to every device.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/matalan-product-discoverability/metrics.png',
          alt: 'Results dashboard showing revenue, time-to-PDP and frustration metrics post-launch',
          caption: 'Post-launch results across desktop and mobile, with mobile load time flagged for follow-up.',
        },
      ],
    },

  },
  
  {
    slug: 'matalan-plp-experience',
    title: 'Improving the product list page experience',
    summary:
      'Designing flexible mobile browsing and faster routes to purchase on Matalan’s product list page.',
    tags: ['E-commerce', 'Mobile', 'Conversion', 'UX research'],
    year: '2024',
    client: 'Matalan',
    role: 'Lead UX Designer',
    thumbnail: {
      src: '/case-studies/matalan-plp-experience/PLP_hero_card.png',
      alt: 'Matalan product list page case study thumbnail',
      gradient: ['#0a0a0a', '#2a6df4'],
    },
    hero: {
      src: '/case-studies/matalan-plp-experience/PLP_hero.png',
      alt: 'Matalan PLP. Column toggle, image carousel and quick-buy drawer on mobile',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'As part of Matalan’s migration to the THG platform, 17 separate tickets had been raised against the product list page, each detailing features and user requirements that needed to land in the new build.',
        'The headline asks were a toggle between two-column and single-column views for mobile browsing, and a streamlined route to purchase that made the journey shorter and more efficient.',
        'How might we improve the mobile PLP, making products easier to browse and faster to buy from, by introducing flexible viewing options and a more efficient purchase path?',
      ],
    },
    approach: {
      heading: 'Approach',
      paragraphs: [
        'Matalan provided a clear vision for the PLP feature set and how it should function for their customers. Analysis of leading e-commerce retailers, confirmed that column-view switching is standard on desktop, but far less common on mobile.',
        'Preference testing in UserZoom let us hear directly from the audience. Figma prototypes presented the column variants and the toggle, with initial-expectation framing followed by think-aloud tasks to capture both attitudinal and behavioural feedback.',
        'The single-column layout frustrated participants. The longer page meant excessive scrolling. They preferred larger images on the product page (PDP) and more products visible at once on the PLP. The two-column view met expectations, balancing clarity with density.',
        'Despite the testing favouring two-column, Matalan were keen to explore whether a single-column layout could still enhance the experience by mirroring the familiar social media feed patterns. With clear requirements in hand, we moved straight to high-fidelity to use design time efficiently.',
      ],
      bullets: [
        'Discovery: 17-ticket PLP backlog reviewed and themed',
        'Competitor benchmarking. Desktop column toggling well established, mobile uncommon',
        'Preference testing in UserZoom with Figma prototypes (think-aloud)',
        'Single-column layout caused scroll fatigue; two-column met expectations',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/matalan-plp-experience/2and1_column.png',
          alt: 'Two-column and single-column PLP layout options shown side-by-side for preference testing',
          caption: 'Preference-testing prototypes: two-column vs. single-column layouts on mobile.',
        },
      ],
    },
    solution: {
      heading: 'Solution',
      paragraphs: [
        'In close collaboration with both THG and Matalan’s designers, wireframes resolved into three focused features:',
        'Two-to-one column switching, letting users opt into larger, full-device-width product imagery on mobile, when they prefer to browse one product at a time.',
        'Image carousel, giving merchandisers a way to surface multiple product images on the PLP, so important features land earlier in the purchase journey rather than only on the PDP.',
        'Quick-buy bottom drawer, letting customers buy directly from the PLP, selecting colour, fit and size variants without ever visiting the product page.',
        'Multiple prototypes were produced. Each feature isolated in its own prototype, plus a combined prototype with all three working together. Splitting them out let us read perceived value per feature, and balance that against build cost when the team prioritised the roadmap.',
        'Usability testing recruited 30 participants matching Matalan’s target audience via UserZoom, working through task-based scenarios while sharing feedback in real time. 80% of participants found the multi-image carousel intuitive and familiar. The quick-buy was received positively, with a useful caveat: 14% said they’d still go to the PDP for clothing purchases to read reviews before committing.',
      ],
      bullets: [
        'Three core features: column toggle, image carousel, quick-buy drawer',
        'Multiple isolated prototypes + one combined, value tested per feature',
        'Usability test: 30 participants, UserZoom, task-based + think-aloud',
        '80% found multi-image carousel intuitive and familiar',
        '14% preferred to visit PDP for clothing, to read reviews pre-purchase'
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/matalan-plp-experience/ideation.png',
          alt: 'Wireframe exploration showing the three PLP features: column toggle, image carousel and quick-buy drawer',
          caption: 'Design exploration. Column toggle, image carousel and quick-buy drawer wireframed for the PLP.',
        },
        {
          kind: 'image',
          src: '/case-studies/matalan-plp-experience/Figjam-collab.png',
          alt: 'Figjam board showing designs and JIRA ticket information for the PLOP tickets.',
          caption: 'Collaborative client workshop with stakeholders. Reviewing designs against JIRA ticket requirements.',
        },
        {
          kind: 'video',
          src: '/case-studies/matalan-plp-experience/single_column.mp4',
          alt: 'Mobile prototype walkthrough showing the column toggle, carousel and quick-buy in use',
          caption: 'Mobile prototype. The column toggle, image carousel and quick-buy drawer working together.',
        },
      ],
    },
    metrics: [
      { label: 'Column-toggle interactions', value: '53k', detail: 'first 3 months · women’s blazers' },
      { label: 'Carousel familiarity', value: '80%', detail: 'of usability participants' },
   
      { label: 'PLP backlog tickets', value: '17', detail: 'addressed in the migration' },
    ],
    impact: {
      heading: 'Impact',
      paragraphs: [
        'A phased delivery approach was agreed to hit the migration deadline. Phase 1 shipped the two-to-one column switching to the live site, with Content Square used to monitor engagement.',
        'In the first three months post-launch, the column toggle saw almost 53,000 interactions in women’s blazers alone, one of the most-visited categories in the womenswear range. The image carousel and quick-buy drawer have since been built and validated in UAT, ready to deploy at the client’s request.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/matalan-plp-experience/CSdata.png',
          alt: 'Content Square data showing post-launch engagement with the PLP column toggle',
          caption: 'Content Square data. Early post-launch engagement with the column toggle on mobile PLP.',
        },
      ],
    },
  
  },

  
  {
    slug: 'driving-quality-at-scale',
    title: 'Driving quality at scale',
    summary:
      'A master Figma template to standardise every project making design documents easier to navigate for designers, engineers and stakeholders.',
    tags: ['Design ops', 'Figma', 'Process', 'Documentation'],
    year: '2024',
    client: 'THG UX team',
    role: 'Lead UX Designer',
    thumbnail: {
      src: '/case-studies/driving-quality-at-scale/quality_card.png',
      alt: 'Driving quality at scale — Figma template case study thumbnail',
      gradient: ['#0a0a0a', '#22a36b'],
    },
    hero: {
      src: '/case-studies/driving-quality-at-scale/quality_hero.png',
      alt: 'Master Figma template pages, naming convention and behaviour annotations',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'How do we ensure a consistent approach to ways of working, with a UX team sat across multiple verticals?',
        'Stakeholders at THG rely on shared Figma files to do their jobs, but with varying levels of familiarity. As designers and researchers, navigating a Figma file is second nature. For everyone else, Figma was relatively new and their technical comfort varied.',
        'The audience for our design documents included UX designers and researchers, external designers, engineering teams, product managers, business analysts, the accessibility team, senior leadership, the data and machine learning team, client partners and project managers. That list kept growing.',
        'The brief: how can we improve our design documents to provide an easy-to-use format for both designers and stakeholders?',
      ],
    },
    approach: {
      heading: 'Approach',
      paragraphs: [
        'A collaborative discovery workshop with the team surfaced the recurring pain points. Some came from designers, some from stakeholders, but all pointed at the same root cause: every project was being structured a little differently, so every reader had to relearn the file each time.',
        'From there, we mapped potential solutions across nine themes. Ways of working, Requirements, Sections, Page structure, Interactivity, Prototypes, File organisation, Design system and Naming conventions. The pattern that emerged was that a master template would address most of the issues at source: by making the structure itself the answer, individual designers wouldn’t have to invent it project by project.',
      ],
      bullets: [
        'Inconsistencies between Figma files (components unlinked, structure varied)',
        'Hard to navigate. Designs not always linked, frames hard to reference',
        'Hard to tell what’s in progress vs. signed off and ready for development',
        'Disconnect between requirements and Figma, kept in separate documents',
        'Design intention, flow direction and interaction behaviour difficult to read',
        'Stakeholders couldn’t reconstruct context, user thinking or prior steps',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/workshop.png',
          alt: 'Snapshot from the discovery workshop showing pain points clustered into themes',
          caption: 'Discovery workshop output. Pain points across designers, engineering, product and leadership.',
        },
      ],
    },
    solution: {
      heading: 'Solution',
      paragraphs: [
        'A comprehensive master Figma template. The starting point for every project a designer works on. Each file is identifiable from a glance, using a standardised naming convention and a colour palette assigned to the workstream, so the right file is easy to find across a portfolio of dozens.',
        'The file itself is a series of pages, ordered by importance, with the most-referenced pages at the top. Clear naming and signposting help users who aren’t familiar with Figma find what they need without having to ask.',
        'Each page does one job:',
        'Signed-off designs. The area for client-approved wireframes. Includes clickable prototypes so stakeholders and engineers can experience the intended user journey and behaviour, not just static frames.',
        'Testing insights. Usability session notes, analysis and key findings. Stakeholders see the thinking behind the solution; designers learn from how their colleagues run studies.',
        'Research and testing prototypes. Interactive prototypes for usability testing through the design phase. Each flow lives in a clearly labelled section, with version notes documenting major changes. Final versions are copied into the engineering handoff pages once they’re ready to build.',
        'Playground. The ideation phase, free from rules and restrictions. Experimental ideas, exploratory work, and earlier iterations live here so nothing useful gets lost.',
        'Discovery. Initial research, peer reviews and collaborative discovery workshops. Designers can see how colleagues have run studies and lift methods across the team.',
        'Best practice is baked into the template itself: simple, clear screen numbering (page + state — 1.0, 1.1, 2.0…), behaviour notes pinned to the right of every frame to call out animations, interactions and usability rules, and user-flow noodles connecting screens. The behaviour annotations are particularly valuable for non-UK engineering teams. Context lives next to the design instead of in a separate doc someone has to find.',
      ],
      bullets: [
        'Standardised naming convention + workstream colour palette per file',
        'Pages ordered by importance with clear signposting',
        'Signed-off designs, testing insights, prototypes, playground, discovery — separated by intent',
        'Screen numbering: page + state (1.0, 1.1, 2.0…) — no “which frame are we on?”',
        'Behaviour notes to the right of every frame for animations, interactions and rules',
        'Status-marked sections so readers can tell signed-off work from in-progress',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/anatomy.png',
          alt: 'Figma template. File cover sheets with workstream colour palette and naming',
          caption: 'Cover sheets. Standardised naming and workstream colour make every file identifiable at a glance.',
        },
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/naming.png',
          alt: 'Figma template. File anatomy showing page order and signposting',
          caption: 'File anatomy. Pages ordered by importance, clearly signposted for non-Figma-fluent readers.',
        },
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/signoff.png',
          alt: 'Figma template. Approved-for-engineering pages with clickable prototypes',
          caption: 'Signed-off designs. Client-approved wireframes paired with clickable prototypes for engineering.',
        },
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/testing-insights.png', 
          alt: 'Figma template. Testing insights section with session notes and key findings',
          caption: 'Testing insights. Session notes, analysis and findings, surfacing the “why” behind decisions.',
        },
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/prototypes.png', 
          alt: 'Figma template. Research and testing prototypes section with version history',
          caption: 'Research prototypes. Clearly labelled flows with version notes, copied into handoff once final.',
        },
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/playground.png', 
          alt: 'Figma template. Playground / ideation section housing experimental and archived designs',
          caption: 'Playground. Ideation, experimental work and archived iterations kept on the record.',
        },
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/discovery.png',
          alt: 'Figma template. Discovery section showing peer review and research methodologies',
          caption: 'Discovery. Initial research and peer reviews, so designers learn from each other’s methods.',
        },
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/behaviour.png',
          alt: 'Behaviour mark-up notes alongside frames, with user-flow noodles connecting screens',
          caption: 'Behaviour notes and flow noodles. Context next to the frame, not buried in a separate doc.',
        },
      ],
    },
    /*metrics: [
      {
        label: 'Engineering clarity requests',
        value: '24%',
        detail: 'on approved workflows',
      },
      {
        label: 'Consistently structured files',
        value: '+67%',
        detail: 'across the team',
      },
      {
        label: 'Time to start a new project',
        value: '17%',
        detail: 'from kick-off to first frame',
      },
    ], */
    impact: {
      heading: 'Impact',
      paragraphs: [
        'The template has been adopted across new projects. Every new project starts from it (for the most part.) Old habits creep in, and fast turnaround projects can slip through the net.',
        'Stakeholders have familiarity with the template. Engineering teams (including non-UK squads) stopped chasing context, because behaviour and intent live next to the frame they describe.',
        'Designers benefit too: the playground and testing pages turn each file into a record of how decisions were reached, so colleagues can lift methods rather than reinvent them. Onboarding new starters is materially faster, since the template is the starting point.',
        'Most importantly, quality stops being a personal habit and becomes a property of the system. Consistency across files climbed 67%, engineering came back asking for clarification 24% less often on approved workflows, and the time it takes to spin up a new project fell by 17%.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/driving-quality-at-scale/impact.png',
          alt: 'Results dashboard showing the three headline metrics post-rollout',
          caption: 'Headline results: –24% engineering clarification, +67% file consistency, –17% project ramp-up.',
        },
      ],
    },

  },

  // ──────────────────────────────────────────────────────────────────────
  // CREATIVE ARCHIVE
  // Visual / creative-led work. Lives in the second homepage grid.
  // ──────────────────────────────────────────────────────────────────────
  {
    slug: 'bbc-pitch-website',
    category: 'creative',
    title: 'BBC Pitch website',
    summary:
      'An online platform for UK production companies and BBC in-house teams to submit content proposals, streamlining the BBC commissioning process.',
    tags: ['Visual design', 'Web', 'BBC'],
    year: '2016',
    client: 'BBC',
    role: 'Visual Designer',
    thumbnail: {
      src: '/case-studies/bbc-pitch-website/bbc-000.jpg',
      alt: 'BBC Pitch website — case study thumbnail',
      gradient: ['#0a0a0a', '#cc1d20'],
    },
    hero: {
      src: '/case-studies/bbc-pitch-website/bbc-000.jpg',
      alt: 'BBC Pitch website — landing screen',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'BBC Pitch is an online platform designed for registered UK-based television production companies, and BBC in-house production teams, to submit content proposals for BBC network television. As part of BBC Television’s commitment to streamlining the commissioning process, BBC Pitch enables Commissioners to review programme ideas more efficiently.',
        'After registering for an account on BBC Pitch, users can submit content proposals directly through a dedicated dashboard. Proposals are categorised by genre and submitted to the appropriate Commissioner. Each submission is securely logged with a unique proposal number for tracking and reference. Users also have the flexibility to save drafts and return to their submissions at any time.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-001.jpg',
          alt: 'BBC Pitch — primary interface screen',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-002.jpg',
          alt: 'BBC Pitch — proposal submission screen',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-003.jpg',
          alt: 'BBC Pitch — dashboard view',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-004.jpg',
          alt: 'BBC Pitch — proposal detail view',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-005.jpg',
          alt: 'BBC Pitch — Commissioner review screen',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-006.jpg',
          alt: 'BBC Pitch — drafts and saved submissions',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-007.jpg',
          alt: 'BBC Pitch — secondary interface detail',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-pitch-website/bbc-008.jpg',
          alt: 'BBC Pitch — additional design detail',
        },

      ],
    },
  },

  {
    slug: 'bbc-stargazing-calendar',
    category: 'creative',
    title: 'BBC Stargazing Calendar',
    summary:
      'A 12-month calendar and event pack for the fourth season of BBC Stargazing Live.',
    tags: ['Visual design', 'Campaign', 'Print', 'BBC'],
    year: '2014',
    client: 'BBC Learning',
    role: 'Visual Designer',
    thumbnail: {
      src: '/case-studies/bbc-stargazing-calendar/stargazing-000.jpg',
      alt: 'BBC Stargazing Calendar — case study thumbnail',
      gradient: ['#0a0a0a', '#0096FA'],
    },
    hero: {
      src: '/case-studies/bbc-stargazing-calendar/stargazing-000.jpg',
      alt: 'BBC Stargazing Calendar — campaign artwork',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'BBC Learning tasked us to produce a range of web resources and promotional materials to assist the fourth season of its popular Stargazing Live programme.',
        'The primary task was to produce a 12-month Calendar for people at three Stargazing Live events, at Jodrell Bank in Cheshire, East Anglia and Northern Ireland. There was also a print-friendly downloadable version for people to use at home, also available in Welsh. The Calendar had several purposes: to educate, stimulate stargazing, teach people when to use a telescope and test knowledge through a series of intriguing questions, with the aim of making it fun, attention-grabbing, functional and practical.',
        'To ensure the events and the website would feel connected to the three programmes, I produced an event pack containing promotional postcards, editable posters and a quick guide to the stars. All supported by downloadable materials from the Stargazing Live website.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/bbc-stargazing-calendar/stargazing-001.jpg',
          alt: 'BBC Stargazing Calendar — design detail',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-stargazing-calendar/stargazing-002.jpg',
          alt: 'BBC Stargazing Calendar — campaign artwork detail',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-stargazing-calendar/stargazing-003.jpg',
          alt: 'BBC Stargazing Calendar — final layout',
        },
      ],
    },
  },

  {
    slug: 'mid-cheshire-college-website',
    category: 'creative',
    title: 'Mid Cheshire College website',
    summary:
      'A user-centred redesign for five distinct visitor groups, modern editorial style, colour-coded faculties, and fully responsive across devices.',
    tags: ['Visual design', 'Web', 'Editorial', 'Education'],
    year: '2016',
    client: 'Mid Cheshire College',
    role: 'Visual Designer',
    thumbnail: {
      src: '/case-studies/mid-cheshire-college-website/mcc-000.jpg',
      alt: 'Mid Cheshire College website — case study thumbnail',
      gradient: ['#0a0a0a', '#22a36b'],
      position: 'top',
    },
    hero: {
      src: '/case-studies/mid-cheshire-college-website/MCCbanner.png',
      alt: 'Mid Cheshire College website — hero banner',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'The redesign of the Mid Cheshire College website focused on creating a user-centric experience for its five primary visitor groups: prospective students, current students, staff, parents, and community partners. The goal was to guide each group through a personalised journey, making it easy to find relevant information tailored to their needs.',
        'The website embraced a modern editorial style, featuring dynamic and regularly updated content through blog posts, news feeds, and integrated social media feeds. This helped maintain a fresh and engaging online presence. Video content played a key role in aiding prospective learners by providing insight into courses, helping them make informed decisions before applying online.',
        'To enhance navigation, a colour-coded system was introduced for the different faculties, a design choice that extended into the college’s printed materials. This cohesive approach allowed visitors to easily orient themselves within the site, providing a clear sense of where they were at all times.',
        'The website was built to be fully responsive, ensuring a seamless experience across devices. For mobile users, data-heavy content was optimised and simplified, prioritising speed and usability without compromising the overall experience. This redesign not only improved the site’s visual appeal but also created a more intuitive and efficient user journey across all platforms.',
      ],
      mediaLayout: '2col',
      media: [
        {
          kind: 'image',
          src: '/case-studies/mid-cheshire-college-website/mcc-000.jpg',
          alt: 'Mid Cheshire College — full-page design mockup',
        },
        {
          kind: 'image',
          src: '/case-studies/mid-cheshire-college-website/mcc-002.jpg',
          alt: 'Mid Cheshire College — full-page design mockup',
        },
        {
          kind: 'image',
          src: '/case-studies/mid-cheshire-college-website/course_information.jpg',
          alt: 'Mid Cheshire College — full-page design mockup',
        },
        {
          kind: 'image',
          src: '/case-studies/mid-cheshire-college-website/mobile.jpg',
          alt: 'Mid Cheshire College — full-page design mockup',
        },
      ],
    },
  },

  {
    slug: 'sepa-floodline-campaign',
    category: 'creative',
    title: 'SEPA Floodline Campaign',
    summary:
      'Launching a free flood-warning service to over 100,000 at-risk Scottish homes. A multi-channel campaign across direct mail, radio, press, outdoor, roadshows and online.',
    tags: ['Visual design', 'Campaign', 'Print', 'Public sector'],
    year: '2014',
    client: 'Scottish Environmental Protection Agency (SEPA)',
    role: 'Visual Designer',
    thumbnail: {
      src: '/case-studies/sepa-floodline-campaign/sepa-000.jpg',
      alt: 'SEPA Floodline Campaign — case study thumbnail',
      gradient: ['#0a0a0a', '#0096FA'],
    },
    hero: {
      src: '/case-studies/sepa-floodline-campaign/sepa-000.jpg',
      alt: 'SEPA Floodline Campaign. Campaign artwork',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        'With at least 100,000 homes at risk of flooding in Scotland, the Scottish Environmental Protection Agency wanted a new service that would provide direct flood warnings, free of charge, to registered landlines and mobiles. A comprehensive campaign utilising direct mail, radio, press, outdoor media, roadshows and online was created to launch the service.',
      ],
      media: [
        {
          // Video — paste the Vimeo URL into `src` to enable playback.
          // The component auto-detects Vimeo URLs and embeds them.
          kind: 'video',
          src: 'https://vimeo.com/87260200?fl=pl&fe=cm',  
          alt: 'SEPA Floodline Campaign — broadcast / online film',
          caption: 'Campaign film',
        },
        {
          kind: 'image',
          src: '/case-studies/sepa-floodline-campaign/sepa-001.jpg',
          alt: 'SEPA Floodline Campaign — campaign artwork',
        },
        {
          kind: 'image',
          src: '/case-studies/sepa-floodline-campaign/sepa-002.jpg',
          alt: 'SEPA Floodline Campaign — campaign artwork',
        },
        {
          kind: 'image',
          src: '/case-studies/sepa-floodline-campaign/sepa-003.jpg',
          alt: 'SEPA Floodline Campaign — campaign artwork',
        },
      ],
    },
  },

  {
    slug: 'bbc-your-paintings',
    category: 'creative',
    title: 'BBC Your Paintings',
    summary:
      'A multi-channel awareness campaign for the BBC website cataloguing 210,000+ UK oil paintings. Award-winning posters, postcards, education packs and nationwide projections.',
    tags: ['Visual design', 'Campaign', 'Print', 'BBC'],
    year: '2013',
    client: 'BBC',
    role: 'Visual Designer',
    thumbnail: {
      src: '/case-studies/bbc-your-paintings/yourpaintings-001.jpg',
      alt: 'BBC Your Paintings — case study thumbnail',
      gradient: ['#0a0a0a', '#cc1d20'],
    },
    hero: {
      src: '/case-studies/bbc-your-paintings/yourpaintings-001.jpg',
      alt: 'BBC Your Paintings. Campaign artwork',
    },
    overview: {
      heading: 'Overview',
      paragraphs: [
        '‘Your Paintings’ is a BBC website cataloguing over 210,000 oil paintings from the UK. This awareness campaign was created to launch the website, telling the nation about the stories behind the paintings, making a connection with the art and their everyday lives, and encouraging them to get involved in tagging the works of art on the site.',
        'This was achieved by a multi-channel campaign, including posters, postcards, worksheets for educational distribution, and art being projected onto buildings nationwide simultaneously.',
        'The posters were awarded Gold at Fresh Design Awards 2013, Silver at the Roses Design Awards 2013, and picked up a commendation at the Drum Design Awards 2013.',
      ],
      media: [
        {
          kind: 'image',
          src: '/case-studies/bbc-your-paintings/yourpaintings-001.jpg',
          alt: 'BBC Your Paintings. Campaign artwork',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-your-paintings/yourpaintings-002.jpg',
          alt: 'BBC Your Paintings. Campaign artwork',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-your-paintings/yourpaintings-003.jpg',
          alt: 'BBC Your Paintings. Campaign artwork',
        },
        {
          kind: 'image',
          src: '/case-studies/bbc-your-paintings/postcards.jpg',
          alt: 'BBC Your Paintings. Campaign artwork',
        },
      ],
    },
  },
];

/** Look up a case study by slug. */
export function getCaseStudy(slug: string | undefined): CaseStudy | undefined {
  if (!slug) return undefined;
  return caseStudies.find((c) => c.slug === slug);
}
